import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Lightbulb, Video, CheckCircle2, XCircle, BookOpen, Eye, FileQuestion } from 'lucide-react';
import { Button } from './ui/button';
import { MathText, springs } from '../lib/utils-app';

export function PreRevealBar({ selected, hasOptions, onReveal }) {
  if (!hasOptions) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={springs.soft}
        className="mt-5 flex flex-wrap items-center gap-3"
      >
        <Button onClick={onReveal}
          className="bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-medium">
          <Eye className="w-4 h-4 mr-1.5" /> Reveal answer & solution
        </Button>
        <span className="text-[12px] text-slate-500">Subjective — no options to pick</span>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={springs.soft}
      className="mt-5 flex flex-wrap items-center gap-3"
    >
      <Button onClick={onReveal} disabled={!selected}
        className="bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-medium disabled:bg-white/[0.04] disabled:text-slate-500 disabled:cursor-not-allowed">
        <Sparkles className="w-4 h-4 mr-1.5" />
        {selected ? 'Reveal Answer' : 'Select an option to reveal'}
      </Button>
      {selected && (
        <span className="text-[12px] text-slate-400">You picked <span className="font-mono text-slate-200">{selected}</span></span>
      )}
    </motion.div>
  );
}

export function ResultBanner({ correct, correctLetter, hasSelection, hasOptions, hasCorrectAnswer }) {
  if (!hasCorrectAnswer) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={springs.soft}
        className="mt-5 rounded-xl p-4 ring-1 ring-amber-400/20 bg-amber-400/[0.05] flex items-start gap-3"
      >
        <FileQuestion className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-serif-q text-[17px] text-amber-100 tracking-tight">Answer key not recorded.</div>
          <div className="text-[13px] text-slate-400 mt-0.5">This entry doesn’t have an official answer yet — read the solution below to verify.</div>
        </div>
      </motion.div>
    );
  }
  if (!hasOptions) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={springs.soft}
        className="mt-5 rounded-xl p-4 ring-1 ring-indigo-400/20 bg-indigo-400/[0.05] flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-serif-q text-[17px] text-indigo-100 tracking-tight">Answer</div>
          <div className="font-serif-q text-[15px] text-slate-100 mt-1 leading-relaxed"><MathText text={correctLetter} /></div>
        </div>
      </motion.div>
    );
  }
  // MCQ result: correct, incorrect-with-selection, or no selection
  let headline;
  if (correct) {
    headline = 'Correct.';
  } else if (hasSelection) {
    headline = `Not this time — the correct answer is ${correctLetter}.`;
  } else {
    headline = `Correct answer is ${correctLetter}.`;
  }
  const toneRing = correct ? 'bg-emerald-400/[0.06] ring-emerald-400/20' : 'bg-rose-400/[0.06] ring-rose-400/20';
  const toneText = correct ? 'text-emerald-100' : 'text-rose-100';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={springs.soft}
      className={`mt-5 rounded-xl p-4 ring-1 flex items-start gap-3 ${toneRing}`}
    >
      {correct ? <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />}
      <div className="flex-1">
        <div className={`font-serif-q text-[17px] ${toneText} tracking-tight`}>{headline}</div>
        <div className="text-[13px] text-slate-400 mt-0.5">Would you like to walk through the solution?</div>
      </div>
    </motion.div>
  );
}

export function SolutionPrompt({ onShow, hasSolution }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.soft, delay: 0.05 }}
      className="mt-4 flex flex-wrap items-center gap-2"
    >
      <Button onClick={onShow} disabled={!hasSolution}
        variant="outline"
        className="border-indigo-400/30 bg-indigo-400/[0.06] text-indigo-200 hover:bg-indigo-400/10 hover:text-indigo-100 disabled:opacity-40">
        <Lightbulb className="w-4 h-4 mr-1.5" />
        {hasSolution ? 'Yes, walk me through it' : 'No solution available yet'}
      </Button>
      <Button variant="outline" disabled title="Coming soon"
        className="border-white/8 bg-white/[0.02] text-slate-500">
        <Video className="w-4 h-4 mr-1.5" />
        Video solution
        <span className="ml-2 text-[9.5px] tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-400 font-semibold uppercase">Soon</span>
      </Button>
    </motion.div>
  );
}

export function SolutionPanel({ solution, correctLetter, onHide }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      exit={{ opacity: 0, y: 6, height: 0 }}
      transition={{ type: 'spring', stiffness: 220, damping: 28 }}
      className="mt-5 overflow-hidden"
    >
      <div className="rounded-2xl border border-indigo-400/15 bg-gradient-to-br from-indigo-500/[0.07] to-violet-500/[0.04] p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-indigo-500/15 ring-1 ring-indigo-400/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-indigo-200" />
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-indigo-200">Stepwise Solution</div>
          {correctLetter && <div className="ml-auto text-[11px] text-slate-400">Answer · <span className="font-mono text-indigo-200">{correctLetter}</span></div>}
        </div>
        <div className="font-serif-q text-[16px] leading-[1.75] text-slate-100">
          <MathText text={solution} />
        </div>
        <button onClick={onHide} className="mt-4 text-[11.5px] text-slate-500 hover:text-slate-300">Collapse</button>
      </div>
    </motion.div>
  );
}

export function RevealFlow({
  state, isCorrect, correctLetter, selected, solution, hasOptions, hasCorrectAnswer, hasSolution,
  onReveal, onShowSolution, onHideSolution,
}) {
  return (
    <div className="mt-2">
      <AnimatePresence mode="wait">
        {state === 'pre' && (
          <motion.div key="pre" exit={{ opacity: 0 }}>
            <PreRevealBar selected={selected} hasOptions={hasOptions} onReveal={onReveal} />
          </motion.div>
        )}
        {(state === 'revealed' || state === 'solution') && (
          <motion.div key="revealed">
            <ResultBanner
              correct={isCorrect}
              correctLetter={correctLetter}
              hasSelection={!!selected}
              hasOptions={hasOptions}
              hasCorrectAnswer={hasCorrectAnswer}
            />
            {state === 'revealed' && <SolutionPrompt onShow={onShowSolution} hasSolution={hasSolution} />}
            <AnimatePresence>
              {state === 'solution' && hasSolution && (
                <SolutionPanel key="sol" solution={solution} correctLetter={correctLetter} onHide={onHideSolution} />
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
